import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
  robots?: string;
  serviceType?: string;
}

const LOCAL_KEYWORDS =
  'Northwest Arkansas, NWA, Fayetteville Arkansas, Springdale Arkansas, Rogers Arkansas, Bentonville Arkansas';

const PAGE_META: Record<string, PageMeta> = {
  '/': {
    title: 'Northwest Arkansas Web, Cloud, Mobile, AI & 3D Printing | Hogcloud',
    description:
      'Hogcloud provides custom-engineered web and mobile applications, cost-efficient cloud architecture and scalable infrastructure, AI agents and prompting, and professional 3D printing in Northwest Arkansas.',
    keywords:
      'custom-engineered web development, cost-efficient website hosting, professional cloud development, mobile app development, AI agents, AI prompting, prompt engineering, custom 3D printing',
  },
  '/about': {
    title: 'About Hogcloud | Experienced Northwest Arkansas Technology Company',
    description:
      'Meet Hogcloud, a Northwest Arkansas technology company with 25+ years of experience in web, cloud and mobile development, AI solutions, and custom 3D printing.',
    keywords: 'Hogcloud experience, Northwest Arkansas technology company, senior cloud architect, local software developer, Fayetteville technology company',
  },
  '/faq': {
    title: 'Frequently Asked Questions | Hogcloud Northwest Arkansas',
    description: 'Get answers to frequently asked questions about Hogcloud websites, hosting, apps, cloud consulting, AI chatbots, email setup, and custom 3D printing.',
    keywords: 'Hogcloud FAQ, Northwest Arkansas web development questions, website pricing, app development, AI chatbot FAQ, custom 3D printing questions',
  },
  '/services': {
    title: 'Technology Services in Northwest Arkansas | Hogcloud',
    description:
      'Explore professional web development and cost-efficient hosting, cloud development, mobile apps, AI agents and prompting, consulting, and custom 3D printing in Northwest Arkansas.',
    keywords: 'Northwest Arkansas technology services, web development, cost-efficient hosting, cloud development, mobile apps, AI agents, prompt engineering, 3D printing',
  },
  '/services/websites': {
    title: 'Professional Web Development & Hosting in Northwest Arkansas | Hogcloud',
    description:
      'Custom-engineered Northwest Arkansas web development and cost-efficient cloud hosting for fast business websites, SEO, e-commerce, scheduling, customer portals, and AI chatbots.',
    keywords: 'professional web development Northwest Arkansas, cost-efficient website hosting, NWA web developer, cloud website hosting, business websites, local SEO, e-commerce development',
    serviceType: 'Web development and website hosting',
  },
  '/services/websites/intelligent-chatbots': {
    title: 'AI Agents & Intelligent Chatbots in Northwest Arkansas | Hogcloud',
    description:
      'Hogcloud builds cloud-hosted AI agents, intelligent website chatbots, SMS support tools, and business knowledge assistants for Northwest Arkansas organizations.',
    keywords: 'AI agents Northwest Arkansas, intelligent chatbots, AI prompting, prompt engineering, website chatbot, SMS chatbot, customer support automation',
    serviceType: 'AI agents and intelligent chatbots',
  },
  '/services/appdev': {
    title: 'Mobile App Development in Northwest Arkansas | Hogcloud',
    description:
      'Professional mobile app development in Northwest Arkansas for customer logins, messaging, scheduling, ordering, service requests, and cloud-connected business tools.',
    keywords: 'mobile app development Northwest Arkansas, professional app developer, NWA app development, iOS app, Android app, cloud mobile application',
    serviceType: 'Mobile app development',
  },
  '/services/consulting': {
    title: 'Experienced Cloud Development & AI Consulting in Northwest Arkansas',
    description:
      'Experienced AWS and cloud development, architecture, AI agent design, prompting, automation, and practical technology consulting for Northwest Arkansas businesses.',
    keywords: 'experienced cloud development Northwest Arkansas, AWS developer NWA, cloud architect, AI agent consulting, AI prompting, prompt engineering, business automation',
    serviceType: 'Cloud development and AI consulting',
  },
  '/services/3dprints': {
    title: 'Custom 3D Printing in Northwest Arkansas | Hogcloud',
    description:
      'Professional custom 3D printing in Northwest Arkansas for business magnets, lithophanes, keychains, signs, event favors, prototypes, badges, and personalized gifts.',
    keywords: '3D printing Northwest Arkansas, custom 3D prints NWA, Fayetteville 3D printing, business magnets, lithophanes, keychains, custom prototypes',
    serviceType: 'Custom 3D printing',
  },
  '/pricing': {
    title: 'Professional Web, Cloud, Mobile & AI Pricing | Northwest Arkansas',
    description:
      'Review Hogcloud pricing for professional websites and cost-efficient hosting, software and mobile development, experienced cloud architecture, AI agents, and prompting support.',
    keywords: 'professional web development pricing, website hosting pricing, cloud development rates, mobile app pricing, AI consulting rates, Northwest Arkansas developer',
  },
  '/contact': {
    title: 'Contact a Northwest Arkansas Web, Cloud & AI Developer | Hogcloud',
    description:
      'Contact Hogcloud in Northwest Arkansas about professional web development and cost-efficient hosting, cloud systems, mobile apps, AI agents and prompting, or custom 3D printing.',
    keywords: 'contact Northwest Arkansas developer, web development quote, hosting quote, cloud developer, mobile app quote, AI agent consultant, 3D printing quote',
  },
  '/schedule': {
    title: 'Schedule a Northwest Arkansas Technology Consultation | Hogcloud',
    description: 'Schedule time with Hogcloud to discuss a Northwest Arkansas web, hosting, cloud, mobile app, AI agent, prompting, or 3D printing project.',
    keywords: 'schedule technology consultation, Northwest Arkansas web consultation, cloud consulting appointment, AI agent consultation',
  },
  '/portfolio': {
    title: 'Web, Cloud, AI & 3D Printing Portfolio | Northwest Arkansas',
    description: 'See Hogcloud work spanning Northwest Arkansas websites, cloud-connected software, AI chatbots, mobile experiences, and custom 3D printed products.',
    keywords: 'Northwest Arkansas web development portfolio, software portfolio, AI chatbot examples, custom 3D printing portfolio',
  },
  '/partners': {
    title: 'Northwest Arkansas Clients, Partners & Community | Hogcloud',
    description: 'Meet Northwest Arkansas businesses, nonprofits, clients, and community partners connected with Hogcloud web, technology, and creative work.',
    keywords: 'Northwest Arkansas business partners, NWA clients, local technology partners, Fayetteville business community',
  },
  '/shop': {
    title: 'Custom 3D Printed Products in Northwest Arkansas | Hogcloud',
    description: 'Shop custom 3D printed gifts, magnets, sports designs, keychains, decorations, party favors, and personalized products made in Northwest Arkansas.',
    keywords: 'custom 3D printed products Northwest Arkansas, NWA 3D printing, personalized gifts, custom magnets, keychains, party favors',
  },
  '/shop/snapcharms': {
    title: '3D Printed SnapCharms in Northwest Arkansas | Hogcloud',
    description: 'Shop Hogcloud SnapCharms: custom 3D printed character charms for keychains, backpacks, necklaces, and gifts made in Northwest Arkansas.',
    keywords: 'SnapCharms, 3D printed charms Northwest Arkansas, character keychains, backpack charms, custom pendants',
  },
  '/shop/sports': {
    title: 'Custom Sports 3D Prints in Northwest Arkansas | Hogcloud',
    description: 'Customize Northwest Arkansas sports-themed 3D printed jerseys, pendants, keychains, magnets, footballs, basketball designs, and fan gifts.',
    keywords: 'sports 3D printing Northwest Arkansas, custom jersey magnets, football keychains, basketball pendants, fan gifts',
  },
  '/shop/locals': {
    title: 'Northwest Arkansas 3D Printed Local Gifts | Hogcloud',
    description: 'Shop 3D printed Northwest Arkansas street signs, city-limits magnets, Fayetteville gifts, and personalized local keepsakes.',
    keywords: 'Northwest Arkansas gifts, Fayetteville Arkansas gifts, local 3D printing, street sign magnets, city limits magnets',
  },
  '/shipping': {
    title: 'Shipping & Local Pickup | Hogcloud Northwest Arkansas',
    description: 'Review shipping and Northwest Arkansas local pickup information for Hogcloud custom 3D printed products.',
    keywords: 'Hogcloud shipping, Northwest Arkansas local pickup, 3D print shipping, Fayetteville pickup',
  },
  '/privacy': {
    title: 'Privacy Policy | Hogcloud Northwest Arkansas',
    description: 'Read the Hogcloud privacy policy for website visitors, customers, and members.',
    keywords: 'Hogcloud privacy policy, Northwest Arkansas technology company privacy',
  },
  '/legal': {
    title: 'Legal Information | Hogcloud Northwest Arkansas',
    description: 'Review legal information for Hogcloud services, software, websites, and custom products.',
    keywords: 'Hogcloud legal information, Northwest Arkansas technology services',
  },
  '/tc': {
    title: 'Terms and Conditions | Hogcloud Northwest Arkansas',
    description: 'Review terms and conditions for Hogcloud technology services and custom products.',
    keywords: 'Hogcloud terms and conditions, Northwest Arkansas technology services',
  },
};

const NOINDEX_META: PageMeta = {
  title: 'Hogcloud | Account and Customer Services',
  description: 'Secure Hogcloud account and customer service page.',
  keywords: LOCAL_KEYWORDS,
  robots: 'noindex, nofollow',
};

const PRODUCT_META: PageMeta = {
  title: 'Custom 3D Printed Product | Hogcloud Northwest Arkansas',
  description: 'View a customizable Hogcloud 3D printed product made in Northwest Arkansas.',
  keywords: 'custom 3D printed product, personalized 3D printing, Northwest Arkansas 3D printing',
};

const setMetaContent = (selector: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    const propertyMatch = selector.match(/meta\[property="([^"]+)"\]/);
    const nameMatch = selector.match(/meta\[name="([^"]+)"\]/);
    if (propertyMatch) element.setAttribute('property', propertyMatch[1]);
    if (nameMatch) element.setAttribute('name', nameMatch[1]);
    document.head.appendChild(element);
  }
  element.content = content;
};

const resolvePageMeta = (pathname: string): PageMeta => {
  if (PAGE_META[pathname]) return PAGE_META[pathname];
  if (pathname.startsWith('/product/')) return PRODUCT_META;
  return NOINDEX_META;
};

const SEO = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageMeta = resolvePageMeta(pathname);
    const canonicalUrl = `https://hogcloud.net${pathname === '/' ? '/' : pathname}`;
    const keywords = `${pageMeta.keywords}, ${LOCAL_KEYWORDS}`;

    document.title = pageMeta.title;
    setMetaContent('meta[name="description"]', pageMeta.description);
    setMetaContent('meta[name="keywords"]', keywords);
    setMetaContent('meta[name="robots"]', pageMeta.robots || 'index, follow');
    setMetaContent('meta[property="og:title"]', pageMeta.title);
    setMetaContent('meta[property="og:description"]', pageMeta.description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[property="og:type"]', 'website');
    setMetaContent('meta[name="twitter:title"]', pageMeta.title);
    setMetaContent('meta[name="twitter:description"]', pageMeta.description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    document.getElementById('route-json-ld')?.remove();
    if (!pageMeta.robots?.startsWith('noindex')) {
      const structuredData = document.createElement('script');
      structuredData.id = 'route-json-ld';
      structuredData.type = 'application/ld+json';
      structuredData.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': pageMeta.serviceType ? 'Service' : 'WebPage',
        name: pageMeta.title,
        description: pageMeta.description,
        url: canonicalUrl,
        areaServed: ['Northwest Arkansas', 'Fayetteville, Arkansas', 'Springdale, Arkansas', 'Rogers, Arkansas', 'Bentonville, Arkansas'],
        ...(pageMeta.serviceType
          ? {
              serviceType: pageMeta.serviceType,
              provider: {
                '@type': 'ProfessionalService',
                name: 'Hogcloud, Inc.',
                url: 'https://hogcloud.net/',
              },
            }
          : {}),
      });
      document.head.appendChild(structuredData);
    }
  }, [pathname]);

  return null;
};

export default SEO;
